import { CALM, NEVER_CONVERTS, weigh } from './suspicion.js';

/**
 * What the building noticed, kept after everybody has forgotten it.
 *
 * This decides nothing. Perception decides who noticed; suspicion decides what
 * they made of it; a person reaching ACCUSING decides whether anything is said.
 * All this does is write down what those three already decided, in the order
 * they decided it, and keep it once they have all moved on.
 *
 * Which is the entire reason it has to exist as its own thing. A witness's
 * record expires after forty-five game minutes, because a person who saw
 * something at two o'clock is not still turning it over at six. A suspicion
 * level drains to nothing on purpose, because the alibi is the whole design.
 * Both of those are right, and between them they mean that by the time a shift
 * ends there is nothing left anywhere in the simulation to read. The thing the
 * player most needs to see is precisely the thing the game is correct to
 * forget.
 *
 * So the ledger is the player's copy, not the witness's. Nobody in the building
 * can consult it and nothing reads it back into the world, with one exception
 * that is the point of him: the man from the ownership group's file on you is
 * this record, read back at the end (`sim/story.js`). He is not in the building
 * the way the staff are, and what he has is what the building noticed.
 *
 * **The near misses are the point.** A record of what you were caught for is a
 * list of write-ups, and there is already one of those. What is missing — and
 * what makes a permissive world teachable rather than merely permissive — is
 * what somebody noticed and never did anything about: who was facing your way,
 * what they thought they had, how sure they got, and how close it came to
 * mattering. Without that, the only lesson the game ever teaches is the one it
 * teaches by ending your run.
 *
 * An act nobody perceived leaves nothing here, because perception never opened
 * a record for it. That is not a rule this file implements; it is the absence
 * of one.
 */

/**
 * What became of one thing somebody noticed.
 *
 *   OPEN       they are still holding it and the shift is not over
 *   FILED      somebody said so and it is on your card
 *   COMPLAINT  a guest said so to somebody else, which costs you differently
 *   NAMED      somebody said so and nothing landed — the same offence, twice,
 *              inside the cooling-off period the file keeps
 *   LET_GO     it came to nothing
 */
export const OUTCOMES = Object.freeze({
  OPEN: 'open',
  FILED: 'filed',
  COMPLAINT: 'complaint',
  NAMED: 'named',
  LET_GO: 'let-go',
});

/** The three that cost you something. Everything else is a near miss. */
export const CONSEQUENCES = Object.freeze([
  OUTCOMES.FILED,
  OUTCOMES.COMPLAINT,
  OUTCOMES.NAMED,
]);

const COSTLY = new Set(CONSEQUENCES);

/**
 * Where each outcome sits on the page. Consequence first, always.
 *
 * A reader with five seconds gets the things that cost them before the things
 * that did not, and an open claim sorts with the near misses because at the
 * moment anybody is reading this the shift is over and an open claim is a claim
 * nobody ever acted on.
 */
const RANK = Object.freeze({
  [OUTCOMES.FILED]: 0,
  [OUTCOMES.COMPLAINT]: 1,
  [OUTCOMES.NAMED]: 2,
  [OUTCOMES.OPEN]: 3,
  [OUTCOMES.LET_GO]: 3,
});

/**
 * Entries kept. Generous: a whole shift is nowhere near it, and the cap is here
 * so a save cannot grow without bound over a long week rather than to ration
 * anything.
 */
const LIMIT = 240;

export function createNoticed({ limit = LIMIT } = {}) {
  /** Keyed by the claim's own id, which is one witness and one act. */
  let entries = new Map();

  /**
   * Opens the entry for a claim, or returns the one already open.
   *
   * Takes the claim rather than an id so that a consequence arriving on the
   * very frame a claim is formed still has somewhere to land. A write-up
   * missing from the record because the bookkeeping was a frame behind the
   * event is exactly the kind of hole this whole module exists to close.
   */
  function open(claim, { day = 1, minutes = 0, nameOf = () => null, onDuty = true } = {}) {
    const existing = entries.get(claim.id);
    if (existing) return existing;
    const entry = {
      id: claim.id,
      by: claim.by,
      /*
       * The name is copied in rather than looked up later, because a summary is
       * a thing that was true on a day and the roster is not promised to be the
       * same next week. Nothing here knows any name until somebody hands it one.
       */
      who: nameOf(claim.by),
      department: claim.department ?? null,
      what: claim.what,
      detail: claim.detail ?? null,
      /** What it was about, when that was not you. Copied, like the rest. */
      subject: claim.subject ?? null,
      /** And their name, when it was a person. */
      about: claim.subject ? nameOf(claim.subject) : null,
      /*
       * How they know. Perception has already resolved this to the strongest
       * way this witness came by the claim, so it is copied rather than
       * compared — there is no second ranking of saw over heard in here.
       */
      mode: claim.mode,
      /** The most sure they ever got. */
      peak: 0,
      /** What that was worth to them, by the file's own table. How close it came. */
      worth: 0,
      /** Whether this witness was ever past CALM while holding it. */
      stirred: false,
      day,
      opened: minutes,
      at: minutes,
      /**
       * Whether you were on the clock when they first noticed. Off the clock
       * nothing they noticed can cost you anything, which is a different kind
       * of near miss and is told differently on the summary.
       */
      onDuty,
      outcome: OUTCOMES.OPEN,
      reason: null,
    };
    entries.set(claim.id, entry);
    return entry;
  }

  /**
   * Oldest and least consequential first.
   *
   * A file on your card outlives the thing nobody ever mentioned, which is the
   * only ordering that makes a capped ledger honest: the entries that drop off
   * the end are the ones whose disappearance changes nothing.
   */
  function trim() {
    if (entries.size <= limit) return;
    const order = [...entries.values()].sort(
      (a, b) => RANK[b.outcome] - RANK[a.outcome] || a.at - b.at
    );
    for (const entry of order) {
      if (entries.size <= limit) break;
      entries.delete(entry.id);
    }
  }

  return {
    get entries() {
      return [...entries.values()];
    },

    /**
     * One frame. Everything anybody currently believes, and what state of mind
     * they are in while believing it.
     *
     * The filter is `weigh`, which is the file's table and not a second one, so
     * the ledger records exactly what a witness would hold against you and
     * nothing else. The noise of setting a round down is a true perception
     * record, weighs nothing, and never appears here — which is the same reason
     * it never makes anybody suspicious.
     *
     * A claim that can never convert anybody — putting something down — is not
     * on it either. It is heard, it weighs five hundredths, and on the summary
     * it was a row about nothing. D's call: clutter.
     */
    observe({ day = 1, minutes = 0, claims = [], stateOf = () => CALM, nameOf, onDuty = true } = {}) {
      for (const claim of claims) {
        if (NEVER_CONVERTS.has(claim.what)) continue;
        const worth = weigh(claim);
        if (worth <= 0) continue;
        const entry = open(claim, { day, minutes, nameOf, onDuty });
        /*
         * A resolved entry is frozen. What it says is what they had at the
         * moment they either spoke up or stopped caring, and a claim that
         * carries on strengthening afterwards does not rewrite what was already
         * decided about it.
         */
        if (entry.outcome !== OUTCOMES.OPEN) continue;
        entry.peak = Math.max(entry.peak, claim.confidence);
        entry.worth = Math.max(entry.worth, worth);
        entry.mode = claim.mode;
        entry.detail = claim.detail ?? entry.detail;
        entry.at = minutes;
        if (stateOf(claim.by) !== CALM) entry.stirred = true;
      }
      trim();
    },

    /**
     * Somebody acted on what they believed. Called by whoever handled the
     * accusation, with whatever actually came of it — which is not always a
     * write-up, because the file has cooling-off periods and this has to report
     * the card as it really reads rather than as the accusation intended.
     */
    file(claim, { outcome = OUTCOMES.FILED, reason = null, day, minutes, nameOf } = {}) {
      const entry = open(claim, { day, minutes, nameOf });
      entry.peak = Math.max(entry.peak, claim.confidence ?? 0);
      entry.worth = Math.max(entry.worth, weigh(claim));
      entry.stirred = true;
      entry.outcome = outcome;
      entry.reason = reason;
      if (minutes !== undefined) entry.at = minutes;
      trim();
      return entry;
    },

    /**
     * The shift is over. Everything still open came to nothing, because the
     * player went home and nobody said anything.
     */
    closeOut() {
      const out = [];
      for (const entry of entries.values()) {
        if (entry.outcome !== OUTCOMES.OPEN) continue;
        entry.outcome = OUTCOMES.LET_GO;
        out.push(entry);
      }
      return out;
    },

    /**
     * One day's record, in reading order: what it cost you, oldest first, then
     * what it did not, closest call first.
     */
    forDay(day) {
      return [...entries.values()]
        .filter((entry) => entry.day === day)
        .sort(
          (a, b) =>
            RANK[a.outcome] - RANK[b.outcome] ||
            (COSTLY.has(a.outcome) ? a.at - b.at : b.worth - a.worth)
        );
    },

    /** Whether an outcome is one that cost something. For whoever is drawing it. */
    costly: (outcome) => COSTLY.has(outcome),

    clear() {
      entries = new Map();
    },

    toJSON: () => ({ entries: [...entries.values()] }),
    load(state) {
      entries = new Map((state?.entries ?? []).map((entry) => [entry.id, entry]));
    },
  };
}
